/**
 * Which in-round bed a round gets.
 *
 * The `play` bed is a rotation across the lounge tracks in {@link PLAY_FILES}:
 * the original `music-play` and those kept of the five added later
 * (`music-play-2` … `music-play-6`, the generator's `21wild-bgm-1` … `-5`).
 * Each round takes the next one from a shuffled bag, so every track comes up
 * once per bag and none twice in a row — including across the join between one
 * bag and the next, where a naive reshuffle repeats the last track one time in N.
 *
 * The bag is kept in `localStorage`, so a reload does not restart it (a player
 * who refreshes between rounds should not hear the same opening bed again), and
 * the *next* track is known ahead of the round: the player fetches it during
 * the results screen instead of at the riffle. Storage is best-effort — blocked,
 * full or corrupt storage costs the memory, never the music.
 *
 * `?bgm=N` pins one track for review: `1`…`5` is the generator's track N
 * (`21wild-bgm-N`, the file `music-play-(N+1)`), `0` is the original
 * `music-play`. A number whose track has been dropped pins nothing. No storage
 * is touched while it is pinned.
 *
 * ## Dropping a track
 *
 * Delete it from {@link PLAY_FILES} and delete its file — nothing else. Both the
 * remembered bag and `?bgm=` are keyed by file NAME, never by position, so a
 * removal cannot remap anyone's saved order or change what a number means.
 * NEVER rename a kept file to close the gap, and never reuse a dropped name for
 * different audio: the mp3s are served `immutable` for 30 days, and a browser
 * that already holds the old bytes under that name would keep playing them.
 * (`music-play-5` was dropped: the fourth generator track, `21wild-bgm-4`.)
 */

/** File stem of each variant. Position is only the bag's working numbering; index 0 is the original bed. */
export const PLAY_FILES: readonly string[] = [
  'music-play',
  'music-play-2',
  'music-play-3',
  'music-play-4',
  'music-play-6',
];

const STORE_KEY = 'wild21.music.play';

/** Something with the bit of `Storage` this needs; `undefined` where there is none. */
export type RotationStore = Pick<Storage, 'getItem' | 'setItem'> | undefined;

/** What is stored: file NAMES, so removing a track cannot shift what a saved entry means. */
interface Saved {
  /** Variants still to come, next first. */
  queue: string[];
  /** The one taken last, so a new bag never opens with it. */
  last: string | null;
}


/** A stored name → its index now, or `null` if it is not (or is no longer) a track. */
const indexOfName = (name: unknown): number | null => {
  if (typeof name !== 'string') return null;
  const i = PLAY_FILES.indexOf(name);
  return i === -1 ? null : i;
};

/**
 * `?bgm=N` → the variant it pins, or `null` when absent, not a number, or naming a track that is not in
 * the rotation. `N` is the generator's number, so it means the same file whatever has been dropped:
 * `1`…`5` is `music-play-(N+1)`, `0` is the original `music-play`.
 */
export function parseBgmOverride(search: string | undefined): number | null {
  if (!search) return null;
  const raw = new URLSearchParams(search).get('bgm');
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return indexOfName(n === 0 ? 'music-play' : `music-play-${n + 1}`);
}

export class PlayRotation {
  readonly #store: RotationStore;
  readonly #rng: () => number;
  readonly #override: number | null;
  #queue: number[] = [];
  #last: number | null = null;

  /**
   * @param store    where the bag is remembered (`localStorage`); may be absent or throw.
   * @param rng      uniform in [0, 1); injected so the shuffle is testable.
   * @param override a pinned variant (see {@link parseBgmOverride}).
   */
  constructor(store: RotationStore, rng: () => number = Math.random, override: number | null = null) {
    this.#store = store;
    this.#rng = rng;
    this.#override = override;
    if (override === null) this.#restore();
  }

  /** The variant the next round will take, without taking it. Stable until {@link take}. */
  peek(): number {
    if (this.#override !== null) return this.#override;
    if (this.#queue.length === 0) {
      this.#queue = this.#bag();
      this.#save();
    }
    return this.#queue[0] as number;
  }

  /** Take the next round's variant. */
  take(): number {
    const next = this.peek();
    if (this.#override !== null) return next;
    this.#queue.shift();
    this.#last = next;
    this.#save();
    return next;
  }

  /** A fresh shuffled bag of every variant that does not open with the one just heard. */
  #bag(): number[] {
    const bag = PLAY_FILES.map((_, i) => i);
    for (let i = bag.length - 1; i > 0; i -= 1) {
      const j = Math.min(Math.floor(this.#rng() * (i + 1)), i);
      [bag[i], bag[j]] = [bag[j] as number, bag[i] as number];
    }
    if (this.#last !== null && bag[0] === this.#last) {
      // Swap the opener with any later slot, so the seam between bags is never a repeat.
      const k = 1 + Math.min(Math.floor(this.#rng() * (bag.length - 1)), bag.length - 2);
      [bag[0], bag[k]] = [bag[k] as number, bag[0] as number];
    }
    return bag;
  }

  #restore(): void {
    try {
      const raw = this.#store?.getItem(STORE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as Partial<Saved>;
      // Names that are no longer tracks (a dropped file, or an older numeric format) fall away.
      const queue = (Array.isArray(saved.queue) ? saved.queue : []).map(indexOfName).filter((i): i is number => i !== null);
      // A bag is a set: a duplicate means the value was not ours, so start over rather than trust it.
      if (queue.length !== new Set(queue).size) return;
      this.#queue = queue;
      this.#last = indexOfName(saved.last);
    } catch {
      /* unreadable: begin a new bag */
    }
  }

  #save(): void {
    try {
      const name = (i: number): string => PLAY_FILES[i] as string;
      this.#store?.setItem(
        STORE_KEY,
        JSON.stringify({ queue: this.#queue.map(name), last: this.#last === null ? null : name(this.#last) } satisfies Saved),
      );
    } catch {
      /* blocked or full: the bag lives for this session only */
    }
  }
}

/** The rotation the game uses: browser storage, the page's `?bgm=`. Safe with no `window`. */
export function defaultRotation(): PlayRotation {
  let store: RotationStore;
  try {
    store = globalThis.localStorage;
  } catch {
    store = undefined;
  }
  return new PlayRotation(store, Math.random, parseBgmOverride(globalThis.location?.search));
}
